import React, { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Settings } from "lucide-react";
import KPITrendChart from "../KPITrendChart/KPITrendChart";
import {
  KPIIncompleteReason,
  KPIUnavailableReason,
} from "../../api/fetchMemberDashboard/types";
import { DashboardKPI, KPIDataSource } from "../../types";
import permissionsService from "@/services/permissionsService";
import "./KPICard.css";

interface KPICardProps {
  kpi: DashboardKPI;
}

/** Kicker on the back face: CertainPath red on white, amber on slate. */
const BACK_EYEBROW =
  "mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary dark:text-amber-400";

/** Matches the page's kicker treatment. See MemberDashboardPage. */
const EYEBROW =
  "text-xs font-semibold uppercase tracking-[0.12em] text-amber-600 dark:text-amber-400 mb-1.5";

/**
 * Why a card has nothing to show, per cause.
 *
 * **Only ever read for a card with no figure.** A card that has one says
 * nothing about how complete it is: the amber note that used to carry a second
 * sentence per cause — "this figure is incomplete" — was removed on 2026-10-01
 * as noise. A member comes to this page for a number, and a caveat that cannot
 * say by how much is a worry rather than a fact.
 *
 * Keyed on the reason the API sends, never defaulted. One sentence for every
 * cause is how a ServiceTitan card came to tell a member their QuickBooks
 * accounts were unmapped.
 */
const INCOMPLETE_COPY: Record<KPIIncompleteReason, string> = {
  unmapped_accounts:
    "This card can't be calculated until your QuickBooks accounts are mapped and confirmed.",
  business_units_without_work_type:
    "This card can't be calculated until your ServiceTitan business units have a work type set.",
  unreadable_job_dates:
    "No job in this period arrived from ServiceTitan with a completion date we could read, so there is nothing to plot.",
};

/**
 * What an empty card says when nothing has explained why it is empty — one
 * sentence per system, because the only honest thing left to say concerns the
 * data the card reads, and these cards read different data.
 *
 * The sibling of `INCOMPLETE_COPY`, reached when there is no reason to key on
 * at all. It replaced a single fallback to the QuickBooks sentence, which is
 * how a ServiceTitan card came to tell a member their chart of accounts was
 * unmapped.
 */
const DEFAULT_UNAVAILABLE_COPY: Record<KPIDataSource, string> = {
  quickbooks:
    "This card can't be calculated until your QuickBooks accounts are mapped and confirmed.",
  servicetitan:
    "There is no ServiceTitan work in this period for this card to measure.",
};

/**
 * Whether the card is showing the member a number anywhere — the headline, or
 * any month of the chart.
 *
 * A headline month that came out null while earlier months have figures is
 * still a card with something on it, so the chart is asked too rather than the
 * headline alone.
 */
const hasFigure = (kpi: DashboardKPI): boolean =>
  kpi.valueLabel !== null || kpi.chart.some((point) => point.value !== null);

/**
 * Names the accounts a card is missing, as the QuickBooks mapping screen names
 * them — that is where the member goes to look for them.
 */
const formatAccountList = (labels: string[]): string => {
  if (labels.length <= 1) {
    return labels[0] ?? "an account this card needs";
  }

  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

/**
 * The kicker over an unavailable card.
 *
 * Four of the five reasons state something about the member that will still be
 * true on the next request, and "Not available" is the right register for
 * those. An outage is ours and clears itself, so it gets a heading that says
 * come back rather than one that reads as a verdict.
 */
const unavailableEyebrow = (kpi: DashboardKPI): string => {
  if (kpi.unavailableReason === "unification_rollup_data_unreachable") {
    return "Temporarily unavailable";
  }

  // An empty card the backend still calls available is one the member has not
  // finished setting up — not a verdict on their books, which is what "Not
  // available" reads as next to a card that has a number.
  return kpi.isAvailable ? "Not available yet" : "Not available";
};

/**
 * Why this card has no figure, in the member's words.
 *
 * Keyed on the reason and never on "unavailable" alone: every reason lands in
 * the same block, and the wrong sentence here tells a member something false
 * about their own books. The fallback is the missing-account case, which is
 * the only one that names data the member controls in QuickBooks — so a new
 * reason added to the API without a branch here must never reach it silently,
 * which is what the switch is for.
 *
 * An empty card with no unavailable reason at all is the member who has mapped
 * nothing: the backend calls it available and incomplete, because availability
 * is asked only once the mapping is finished. The incomplete reason carries the
 * sentence in that case.
 *
 * **With no reason of either kind, the card falls back on its own system.**
 * `incompleteReason` is null on every healthy card on the page, so the previous
 * `?? "unmapped_accounts"` was not a rare edge: it fired on all five
 * ServiceTitan cards whenever a member's period held no work, and told them to
 * go and map a chart of accounts their card never reads.
 */
const emptyCardCopy = (kpi: DashboardKPI): React.ReactNode => {
  if (kpi.unavailableReason === null) {
    return kpi.incompleteReason === null
      ? DEFAULT_UNAVAILABLE_COPY[kpi.dataSource]
      : INCOMPLETE_COPY[kpi.incompleteReason];
  }

  switch (kpi.unavailableReason) {
    /*
      Nothing is unmapped here, so the sentence must not mention mapping. This
      member's books resolved perfectly and their equity is under water —
      telling them to go fix a mapping would send them looking for a problem
      that does not exist.
    */
    case "non_positive_equity":
      return (
        <>
          This ratio divides by your equity, and yours has not been above zero
          in this period. There is no ratio to plot until it is.
        </>
      );

    /*
      The denominator is a burn rate, and theirs is zero or below — no overhead
      recorded this period, or a rebate that netted what there was back out.
      The sentence covers both without naming either: "zero or less" is the
      whole condition, and a member who reads it goes and looks.

      Dividing by it is what produced a headline of minus several million days
      for a member with cash in the bank, so the card shows nothing instead.
    */
    case "non_positive_operating_expenses":
      return (
        <>
          Your operating expenses for this period come to zero or less, so there
          is no daily spend to measure your cash against.
        </>
      );

    /*
      Connected, but nothing has arrived. This card used to report
      `missing_ledger_accounts` here — "you don't keep a Field Labor account" —
      which is a statement about books we have not received, and it sent the
      member to a mapping page that is empty for the same reason.

      The import is started by a person (the integrations page's step 5 says
      "contact the support team"), so this does not clear itself and the copy
      must not imply that waiting is all there is to do.
    */
    case "not_synced_yet":
      return (
        <>
          Your QuickBooks account is connected, but no financial data has
          arrived yet. Your numbers appear here once the first import runs.
        </>
      );

    /*
      Nothing has been summarised for this member yet, so the card has nothing
      to read. Worth its own sentence rather than falling back to an empty
      card: an empty card reads as "you did no work this period", which is an
      answer, and the wrong one. Nothing for the member to do — the nightly
      refresh, the Refresh button, or saving a work type all build it.
    */
    case "servicetitan_not_rebuilt_yet":
      return (
        <>
          Your ServiceTitan numbers are still being prepared. This card fills in
          after the next refresh.
        </>
      );

    /*
      Their ServiceTitan configuration, not their performance. Without a
      bookable reason there is no denominator, and plotting 100% because every
      call we found happened to book would be worse than plotting nothing.

      It no longer tells them to go and mark the reasons, and it carries no
      link. Bookable is a flag on a call reason in ServiceTitan, reaching us
      through unification's ingest — there is no control for it anywhere in hub,
      so the cogwheel this used to show led to a page about credentials and
      business units and left the member hunting.
    */
    case "no_bookable_call_reasons":
      return <>No bookable call reasons found.</>;

    /*
      A ServiceTitan card, so no mention of QuickBooks at all. The member has
      business units; none is marked as the kind of work this card measures,
      which is a setting they can change.
    */
    case "no_work_type_assigned":
      return (
        <>
          None of your ServiceTitan business units is set to{" "}
          {kpi.workTypeLabel ?? "this kind of"} work yet. Set one to see this
          card.
        </>
      );

    /*
      The only reason that is about us rather than about them, and the only one
      that will be gone by the next request. The card is returned at all —
      rather than the section quietly shrinking from fifteen cards to ten —
      because a section that disappears reads as "you have no ServiceTitan".

      It names no vendor, because the failure is never ServiceTitan's: hub
      reads these figures from unification's rollup tables and does not call
      ServiceTitan at all on this path.
    */
    case "unification_rollup_data_unreachable":
      return <>KPI data is temporarily unavailable.</>;

    case "missing_ledger_accounts":
      return (
        <>
          {/*
            "Field Labor" stays bare, with no "account" after it: it is the
            exact option the member picks under the mapping screen's
            "CertainPath account" column, and repeating the word would put
            "account" in one sentence twice with two different referents —
            theirs and ours.
          */}
          No QuickBooks account is mapped to{" "}
          {formatAccountList(kpi.missingAccountLabels)} yet. Please map one to
          see this card.
        </>
      );

    /*
      A reason this file has no sentence for. It used to be the missing-account
      case above, which meant a reason added to the API for the ServiceTitan
      cards would describe a QuickBooks mapping until someone noticed. Falling
      back on the card's own system is wrong in a milder way: vague, but never
      about the wrong product.
    */
    default:
      return DEFAULT_UNAVAILABLE_COPY[kpi.dataSource];
  }
};

/**
 * The screens a card can send a member to, each named more than once below.
 *
 * The QuickBooks page appears twice with two labels: a card that cannot be
 * computed at all asks them to configure the integration, while one whose
 * mapping is unfinished asks for the chart of accounts — the same screen, a
 * different errand.
 */
const QUICKBOOKS_SETTINGS_LINK = {
  href: "/hub/settings/integrations/quickbooks",
  label: "Configure QuickBooks",
};

const QUICKBOOKS_MAPPING_LINK = {
  href: "/hub/settings/integrations/quickbooks",
  label: "Map your chart of accounts",
};

const SERVICETITAN_SETTINGS_LINK = {
  href: "/hub/settings/integrations/servicetitan",
  label: "Configure ServiceTitan",
};

/**
 * Where the member goes to fix this card, or null when nothing on the card is
 * theirs to configure.
 *
 * The nulls are the reasons that name no setting: an outage is ours and clears
 * itself, a rollup nobody has built rebuilds itself, and equity under water is
 * a fact about their books. Sending any of them to an integrations screen would
 * promise a switch that is not there. Every other reason names something behind
 * one of the two screens — the QuickBooks page carries the account mapping, the
 * ServiceTitan page the business units.
 *
 * **Every reason is listed.** The QuickBooks page used to be the `default:`,
 * which is how `servicetitan_not_rebuilt_yet` — added later, with its own
 * sentence and no arm here — came to sit under a "Configure QuickBooks"
 * cogwheel. What is left for the default is a reason nobody has thought about,
 * and it follows the card's own system rather than assuming QuickBooks.
 */
const settingsLink = (
  reason: KPIUnavailableReason | null,
  dataSource: KPIDataSource,
): { href: string; label: string } | null => {
  switch (reason) {
    case "unification_rollup_data_unreachable":
    case "servicetitan_not_rebuilt_yet":
    case "non_positive_equity":
    case "no_bookable_call_reasons":
      return null;

    case "no_work_type_assigned":
      return SERVICETITAN_SETTINGS_LINK;

    case "missing_ledger_accounts":
    case "non_positive_operating_expenses":
    case "not_synced_yet":
      return QUICKBOOKS_SETTINGS_LINK;

    default:
      return dataSource === "quickbooks" ? QUICKBOOKS_SETTINGS_LINK : null;
  }
};

/**
 * The same, for the incomplete state — which is the one a member with nothing
 * mapped actually lands on.
 *
 * `isAvailable` is asked only once the mapping is finished, so a member who has
 * mapped nothing at all never reaches the unavailable block and never sees its
 * link. That leaves the state most in need of a route to the mapping screen as
 * the only one without one, which is why this exists separately rather than
 * both branches sharing `settingsLink`.
 *
 * Unreadable job dates get nothing: no screen the member can open will fix a
 * timestamp that arrived unparseable.
 *
 * **With no reason at all, the system decides.** The mapping page was the old
 * default here, and it travelled with the QuickBooks sentence onto cards that
 * read no QuickBooks data. A ServiceTitan card in that state gets no cogwheel:
 * nothing has named a problem, so nothing has named a screen.
 */
const incompleteSettingsLink = (
  reason: KPIIncompleteReason | null,
  dataSource: KPIDataSource,
): { href: string; label: string } | null => {
  switch (reason) {
    case "unreadable_job_dates":
      return null;

    case "business_units_without_work_type":
      return {
        href: "/hub/settings/integrations/servicetitan",
        label: "Set business unit work types",
      };

    case "unmapped_accounts":
      return QUICKBOOKS_MAPPING_LINK;

    default:
      return dataSource === "quickbooks" ? QUICKBOOKS_MAPPING_LINK : null;
  }
};

/**
 * The screen that fixes an empty card, whichever check left it empty — the
 * unavailable reason when there is one, the incomplete reason when the member
 * simply has not started mapping.
 */
const emptyCardSettingsLink = (
  kpi: DashboardKPI,
): { href: string; label: string } | null =>
  kpi.unavailableReason === null
    ? incompleteSettingsLink(kpi.incompleteReason, kpi.dataSource)
    : settingsLink(kpi.unavailableReason, kpi.dataSource);

/**
 * The card is a button that flips, so both handlers stop here. Enter on the
 * link would otherwise reach the card's own keydown, which calls
 * preventDefault — flipping the card instead of following the link.
 */
const SettingsLink: React.FC<{
  className: string;
  href: string;
  label: string;
}> = ({ className, href, label }) => (
  <Link
    className={`inline-flex items-center gap-1.5 text-xs font-semibold hover:underline ${className}`}
    onClick={(event) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
    to={href}
  >
    <Settings className="h-3.5 w-3.5" />
    {label}
  </Link>
);

const KPICard: React.FC<KPICardProps> = ({ kpi }) => {
  const [flipped, setFlipped] = useState(false);
  const { hasPermission } = permissionsService();

  /*
    Both screens sit behind `integrations`, which the dashboard's own
    `member-dashboard` permission does not imply — a coach has the dashboard
    and not the settings. Offering them a cogwheel would send them into a
    PermissionGuard.
  */
  const canReachSettings = hasPermission("integrations");

  /*
    Whether the card has anything to show, which is what decides the treatment
    — not `isAvailable`, which answers the narrower question of whether the
    backend could compute the card once the mapping was finished. The two came
    apart for the member who has mapped nothing: available, incomplete, and
    rendering a dash over an empty chart with guidance about a trend that does
    not exist. An empty card gets the empty-card treatment however it got empty.
  */
  const showsFigure = kpi.isAvailable && hasFigure(kpi);

  const emptyCardLink = canReachSettings ? emptyCardSettingsLink(kpi) : null;

  const toggle = () => setFlipped((current) => !current);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <div
      aria-label={`${kpi.name}. ${flipped ? "Showing how it is calculated" : "Showing the trend"}. Activate to flip.`}
      aria-pressed={flipped}
      className={`kpi-card cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:focus-visible:ring-slate-500 ${
        flipped ? "is-flipped" : ""
      }`}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="relative">
        <div className="kpi-card__face kpi-card__front">
          <div className="flex min-h-[32rem] flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-start justify-between gap-3">
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                {kpi.source}
              </span>
            </div>

            <div>
              <h3 className="text-xl font-bold tracking-tight text-secondary dark:text-white">
                {kpi.name}
              </h3>
              {/*
                A card with nothing to show shows no headline at all rather than
                a dash. A dash reads as "we looked and found nothing"; this card
                has not looked, because there is nothing yet to look in.
              */}
              {showsFigure && (
                <>
                  <div className="mt-2 flex flex-wrap items-baseline gap-3">
                    <span className="text-5xl font-bold leading-none tracking-tight text-secondary dark:text-white">
                      {kpi.valueLabel ?? "—"}
                    </span>
                    {kpi.deltaLabel && (
                      <span
                        className={`text-sm ${
                          kpi.deltaIsGood
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-red-700 dark:text-red-400"
                        }`}
                      >
                        {kpi.deltaIsUp ? "↑" : "↓"} {kpi.deltaLabel}
                      </span>
                    )}
                  </div>
                  {kpi.monthLabel && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {kpi.monthLabel}
                    </p>
                  )}
                </>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300">
              {kpi.definition}
            </p>

            {showsFigure ? (
              <>
                <KPITrendChart series={kpi.chart} />

                <div className="border-t border-gray-200 pt-4 dark:border-slate-700">
                  <div className={EYEBROW}>What to look for</div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {kpi.guidance}
                  </p>
                </div>
              </>
            ) : (
              /*
                Not an error, and not a warning — nothing here is going wrong.
                Neutral treatment, and the guidance is dropped along with the
                chart: both describe a trend that does not exist.

                The copy describes the *mapping*, never the business. Nothing
                here can tell "you don't track field labor" apart from "you
                mapped your field labor account to Other Direct Cost", and the
                second is the likelier of the two — so it says what is missing
                and how to fix it rather than telling a member what their own
                books contain.
              */
              <div
                className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/50"
                data-testid="kpi-unavailable"
              >
                <div className={EYEBROW}>{unavailableEyebrow(kpi)}</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {emptyCardCopy(kpi)}
                </p>
                {emptyCardLink && (
                  <SettingsLink
                    className="mt-2.5 text-secondary dark:text-white"
                    href={emptyCardLink.href}
                    label={emptyCardLink.label}
                  />
                )}
              </div>
            )}

            <div className="mt-auto flex items-center gap-2 pt-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
              <RefreshCw className="h-4 w-4" />
              Tap card for the formula
            </div>
          </div>
        </div>

        <div className="kpi-card__face kpi-card__back">
          {/*
            Two palettes, one structure. Light mode is a white card with
            CertainPath red as the accent; dark mode borrows the nav shell's
            slate-900 with amber, so the back reads as part of the app.

            The accent has to change with the surface — amber on white is
            roughly 1.7:1 against text, and red on slate-900 is little better.
            Each palette picks the brand colour that survives its background.
          */}
          <div className="flex h-full flex-col gap-5 overflow-auto rounded-lg border border-gray-200 bg-white p-6 dark:border-transparent dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className={BACK_EYEBROW}>How we calculate it</div>
                <h3 className="text-xl font-bold tracking-tight text-secondary dark:text-white">
                  {kpi.name}
                </h3>
              </div>
              <span className="flex-none rounded-full border border-secondary/25 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.07em] text-secondary/70 dark:border-white/20 dark:text-white/60">
                {kpi.source}
              </span>
            </div>

            {/*
              CertainPath red in both themes, so the formula reads the same way
              on a white card as on a slate one. Everything on it is white —
              nothing else clears contrast against this red at body size.
            */}
            <div className="flex items-center justify-center gap-4 rounded-xl bg-primary px-5 py-6">
              {/*
                A KPI with no denominator is a single figure, not a ratio — a
                balance sheet card has nothing to divide by. The rule and the
                bottom line are dropped rather than left empty, so the block
                reads as a statement instead of a fraction missing its half.
              */}
              <div className="flex max-w-xs flex-col items-center text-center">
                <span
                  className={`text-sm font-semibold leading-snug text-white ${kpi.formula.denominator ? "pb-2.5" : ""}`}
                >
                  {kpi.formula.numerator}
                </span>
                {kpi.formula.denominator && (
                  <>
                    <span className="w-full border-t-2 border-white" />
                    <span className="pt-2.5 text-sm font-semibold leading-snug text-white">
                      {kpi.formula.denominator}
                    </span>
                  </>
                )}
              </div>
              {kpi.formula.multiplier && (
                <span className="whitespace-nowrap text-xl font-bold text-white">
                  {kpi.formula.multiplier}
                </span>
              )}
            </div>

            {kpi.formula.workedExample && (
              <div className="rounded-r-lg border-l-[3px] border-primary bg-primary/5 px-4 py-3 dark:border-amber-400 dark:bg-amber-400/10">
                <div className={BACK_EYEBROW}>
                  Your numbers for {kpi.monthLabel}
                </div>
                <p className="text-lg font-semibold text-secondary dark:text-white">
                  {kpi.formula.workedExample}
                </p>
              </div>
            )}

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-white/45">
                What goes into it
              </div>
              <p className="text-sm leading-relaxed text-gray-600 dark:text-white/80">
                {kpi.formula.note}
              </p>
            </div>

            <div className="mt-auto flex items-center gap-2 border-t border-gray-200 pt-4 text-xs font-semibold text-primary dark:border-white/10 dark:text-amber-400">
              <RefreshCw className="h-4 w-4" />
              Tap card to flip back
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPICard;
